import { Buffer } from "buffer";
import { ExecutionStatus } from "config/ts-utils";
import { filterAdvancedResourceTypes } from "containers/AdvancedSearch/utils";
import { WrappedResource } from "containers/Resources/components/CommonActions/interfaces";
import {
    ActivityType,
    BundleItemType,
    MetaType,
    ProjectItemType,
    RecordType,
    Resource,
    ResourceType,
    WorkspaceItemType,
} from "graphql/generated/graphql";
import graphqlSchema from "graphql/generated/graphql.schema.json";
import { useEffect, useRef } from "react";
import { getTypedLowerCase } from "utils/service";

export const getListFromGraphql = (typeName) => {
    const schemaType = (graphqlSchema as any).__schema.types.find(
        (type) => type.name === typeName
    );
    // Defensive: unknown type names (e.g. a "<Container>ItemType" enum that
    // doesn't exist for non-container record types) would otherwise crash on
    // `.enumValues` of undefined and trip the Console error boundary.
    if (!schemaType?.enumValues) {
        return [];
    }
    return schemaType.enumValues.map((enumValue) => enumValue.name);
};

export const filterRecordList = (typeList: any[]) =>
    typeList.filter((type) => type !== "TAG" && type !== "OAUTH" && type !== "PROCESS");
export const resourceList: ResourceType[] = filterRecordList(getListFromGraphql("ResourceType"));
export const recordList: RecordType[] = filterRecordList(getListFromGraphql("RecordType"));
export const activityList: ActivityType[] = filterRecordList(getListFromGraphql("ActivityType"));

export const metaTypeList: MetaType[] = getListFromGraphql("MetaType");

export const bundleItemTypeList: BundleItemType[] = getListFromGraphql("BundleItemType");
export const projectItemTypeList: ProjectItemType[] = getListFromGraphql("ProjectItemType");
export const workspaceItemTypeList: WorkspaceItemType[] = getListFromGraphql("WorkspaceItemType");
export const nonBundleItemTypeList: BundleItemType[] = getListFromGraphql("NonBundleItemType");
export const nonProjectItemTypeList: ProjectItemType[] = getListFromGraphql("NonProjectItemType");
export const nonWorkspaceItemTypeList: WorkspaceItemType[] =
    getListFromGraphql("NonWorkspaceItemType");
export const checkBundleItemType = (type) =>
    bundleItemTypeList.some((item) => item === type.toUpperCase());
export const checkProjectItemType = (type) =>
    projectItemTypeList.some((item) => item === type.toUpperCase());
export const checkWorkspaceItemType = (type) =>
    workspaceItemTypeList.some((item) => item === type.toUpperCase());
export const checkNonBundleItemType = (type) =>
    nonBundleItemTypeList.some((item) => item === type.toUpperCase());
export const checkNonProjectItemType = (type) =>
    nonProjectItemTypeList.some((item) => item === type.toUpperCase());
export const checkNonWorkspaceItemType = (type) =>
    nonWorkspaceItemTypeList.some((item) => item === type.toUpperCase());
export const checkSubrecordType = (type) =>
    getListFromGraphql("SubrecordType").some((item) => item === type.toUpperCase());
export const checkContainerItemType = (type) =>
    getListFromGraphql("ContainerType").some((item) => item === type.toUpperCase());
export const checkConnectorType = (type) =>
    getListFromGraphql("ConnectorType").some((item) => item === type.toUpperCase());
export const checkActivityType = (type) =>
    getListFromGraphql("ActivityType").some((item) => item === type.toUpperCase());
export const filterActivityType = (item) => activityList.includes(item);

export const getRecordTypeFields = (typeName) =>
    (graphqlSchema as any).__schema.types
        .find((type) => type.name === typeName)
        .fields.map((field) => field.name);

export const getResourceLabel = (resource: RecordType) => {
    if (resource === "LDAP_CONFIG") {
        return "LDAP config";
    }
    if (resource === "OAUTH") {
        return "OAuth";
    } else return capitalizeFirstLetter(resource.toLowerCase()).replace("_", " ");
};

export const mapResourceTab = (resource: RecordType) => ({
    uid: getTypedLowerCase(resource),
    label: getResourceLabel(resource),
});

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isUuid = (value) => uuidPattern.test(value);

const contains = (parent, child) => parent !== child && parent?.contains(child);

const useAnyOutsideClick = (contextMenuRef, callback) => {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (contextMenuRef?.current && !contextMenuRef?.current?.contains(event.target)) {
                callbackRef.current();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [contextMenuRef]);
};

const useOutsideClick = (ref, callback) => {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const handleClick = (e) => {
            if (
                ref.current &&
                // A click whose target was detached from the document during its
                // own synchronous React handler (e.g. an in-popover button that
                // replaces itself on click, or the menu item that opened this
                // popover) is NOT an outside click — `contains()` returns false
                // for a detached node and would spuriously close the popover.
                e.target.isConnected &&
                !ref?.current?.contains(e.target) &&
                !e.target.classList.contains("react-select__option") &&
                !e.target.classList.contains("react-select__multi-value__remove") &&
                e.target.getAttribute("role") !== "option"
            ) {
                callbackRef.current();
            }
        };

        const handleKeyPress = (e) => {
            if (e.keyCode === 9) {
                setTimeout(
                    () => !contains(ref.current, document.activeElement) && callbackRef.current(),
                    1
                );
            }
        };

        document.addEventListener("click", handleClick);
        document.addEventListener("keydown", handleKeyPress);
        return () => {
            document.removeEventListener("click", handleClick);
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [ref]);
};
export const useOutsideAutocompleteClick = (ref, uniqueKey, callback) => {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const getCurrentRef = () =>
            ref?.current?.controlRef ? ref?.current?.controlRef : ref?.current;

        const handleClick = (e) => {
            const currentRef = getCurrentRef();
            // When the menu is lifted out of the control (in-modal selects: the
            // react-select option list via menu-portal, or the whole custom-menu
            // via the MUI Popper anchor), a click inside it is not contained by
            // `currentRef`; treat it as inside so interacting with the search box
            // or an option never closes the dropdown.
            if (e.target?.closest?.(".react-select__menu-portal, .react-select__custom-menu")) {
                return;
            }
            if (
                currentRef &&
                !currentRef?.contains(e.target) &&
                e.target.id !== uniqueKey &&
                e.target.parentNode?.id !== uniqueKey &&
                e.target.parentNode?.parentNode?.id !== uniqueKey &&
                e.target.parentNode?.parentNode?.parentNode?.id !== uniqueKey
            ) {
                callbackRef.current();
            }
        };

        const handleKeyPress = (e) => {
            if (e.keyCode === 9) {
                const currentRef = getCurrentRef();
                setTimeout(
                    () => !contains(currentRef, document.activeElement) && callbackRef.current(),
                    1
                );
            }
        };

        document.addEventListener("click", handleClick);
        document.addEventListener("keydown", handleKeyPress);
        return () => {
            document.removeEventListener("click", handleClick);
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [ref, uniqueKey]);
};

const useInsideClick = (ref, callback) => {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const handleClick = (e) => {
            if (
                ref.current &&
                ref?.current?.contains(e.target) &&
                e.target.parentElement.classList.contains("popover__toggle") === false &&
                e.target.parentElement.parentElement.classList.contains("popover__toggle") ===
                    false &&
                (!e.keyCode || e.keyCode === 13)
            ) {
                setTimeout(() => callbackRef.current(), 10);
            }
        };

        document.addEventListener("click", handleClick);
        document.addEventListener("keydown", handleClick);
        return () => {
            document.removeEventListener("click", handleClick);
            document.removeEventListener("keydown", handleClick);
        };
    }, [ref]);
};

/**
 * Collection of `util` helper functions
 */

/**
 * Generate keys for React Components
 * @param {String} string Param that is used for the key
 * @return {String} Returns generated key
 */
const generateKey = (string) => {
    let theKey = "";

    // if FormattedMessage object
    if (string?.constructor === Object) {
        if (string.props && string.props.defaultMessage) {
            string = string.props.defaultMessage;
        }
    }

    // generate key
    theKey = string
        .toString()
        .split("")
        .reduce(
            // eslint-disable-next-line
            (prevHash, currVal) => ((prevHash << 5) - prevHash + currVal.charCodeAt(0)) | 0,
            0
        );

    return theKey;
};

function formatAmPm(hours, minutes) {
    let h = hours;
    let m = minutes;
    const ampm = h >= 12 ? "pm" : "am";
    h %= 12;
    h = h || 12; // the hour '0' should be '12'
    m = m < 10 && m > 0 ? `0${m}` : m;
    const strTime = `${h}:${m} ${ampm}`;
    return strTime;
}

/**
 * Format date
 * @param {string} date
 * @param {string} format
 * @return {String} date
 * */
const formatDate = (date, format = "YYYY-MM-DD hh:mm:ss") => {
    const d = new Date(date);
    const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    function appendZero(time) {
        if (time <= 9) {
            return `0${time}`;
        }
        return time;
    }

    const day = d.getDate();
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    const monthString = months[d.getMonth()]; // months[] is 0-indexed; `month` is 1-based
    const hour = d.getHours();
    const minute = d.getMinutes();
    const second = d.getSeconds();

    switch (format) {
        case "YYYY-MM-DD":
            return `${year}-${appendZero(month)}-${appendZero(day)}`;
        case "YYYY/MM/DD":
            return `${year}/${appendZero(month)}/${appendZero(day)}`;
        case "DD/MM/YYYY":
            return `${appendZero(day)}/${appendZero(month)}/${year}`;
        case "DD-MM-YYYY":
            return `${appendZero(day)}-${appendZero(month)}-${year}`;
        case "Month":
            return monthString;
        case "Day+Month":
            return `${day} ${monthString}`;
        case "Month+Day":
            return `${monthString} ${day}`;
        case "hh:mm:ss":
            return `${appendZero(hour)}:${appendZero(minute)}:${appendZero(second)}`;
        case "hh:mm":
            return `${appendZero(hour)}:${appendZero(minute)}`;
        case "hh:mm AA":
            return formatAmPm(hour, minute);
        case "YYYY-MM-DD hh:mm:ss":
            return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
        case "YYYY-MM-DD hh:mm AA":
            return `${year}-${month}-${day} ${formatAmPm(hour, minute)}`;
        case "YYYY/MM/DD hh:mm:ss":
            return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
        case `YYYY/MM/DD hh:mm AA`:
            return `${year}/${month}/${day} ${formatAmPm(hour, minute)}`;
        default:
            return `${day}-${month}-${year}`;
    }
};

/**
 * Turns Hex into rgba
 * @return {String} rgba
 * */
const hexToRGB = (hex, alpha) => {
    hex = hex.toString().replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    if (alpha) {
        return `rgba(${r},${g},${b},${alpha})`;
    }

    return `rgb(${r},${g},${b})`;
};

// default spacing for app
const spacing = 8;

// max-width breakpoints
// current mobile breakpoint key: 'tablet'
const breakpoints = {
    small: 480,
    mobile: 850,
    tablet: 1030,
    desktop: 1200,
};

const statusColors = {
    [ExecutionStatus.WAITING_ENGINE]: "light-blue",
    [ExecutionStatus.WAITING_DEPENDENCY]: "light-blue",
    [ExecutionStatus.WAITING_LOCK]: "light-blue",
    [ExecutionStatus.WAITING_RECORD]: "light-blue",
    [ExecutionStatus.WAITING_RESPONSE]: "light-blue",
    [ExecutionStatus.AT_ENGINE]: "primary-blue",
    [ExecutionStatus.SCHEDULED]: "light-blue",
    [ExecutionStatus.PAUSED]: "primary-yellow",
    [ExecutionStatus.ENDED_SUCCESS]: "primary-green",
    [ExecutionStatus.ENDED_ERROR]: "primary-red",
    [ExecutionStatus.ENDED_CANCELLED]: "primary-red",
};

const statusLabels = {
    [ExecutionStatus.WAITING_ENGINE]: "Pending",
    [ExecutionStatus.WAITING_DEPENDENCY]: "Waiting for dependency",
    [ExecutionStatus.WAITING_LOCK]: "Waiting for lock",
    [ExecutionStatus.WAITING_RECORD]: "Waiting for record",
    [ExecutionStatus.WAITING_RESPONSE]: "Waiting for response",
    [ExecutionStatus.AT_ENGINE]: "Running",
    [ExecutionStatus.SCHEDULED]: "Scheduled",
    [ExecutionStatus.PAUSED]: "Paused",
    [ExecutionStatus.ENDED_SUCCESS]: "Success",
    [ExecutionStatus.ENDED_ERROR]: "Error",
    [ExecutionStatus.ENDED_CANCELLED]: "Cancelled",
};

export const advancedFilterResourceList = resourceList.filter(filterAdvancedResourceTypes);

const findTerm = (typeList, term) => typeList.some((type) => type === term.toUpperCase());
export const findTermInAdvancedSearchResources = (term) =>
    findTerm(advancedFilterResourceList, term);
export const findTermInActivities = (term) => findTerm(activityList, term);
const findTermInRecords = (term) => findTerm(recordList, term);

export const wrapTermWithWildcards = (term) => `%${term}%`;

export const removeWildcards = (term) => {
    if (term[0] === "%" && term.slice(-1) === "%") {
        return term.slice(1, -1);
    } else if (term.includes("%")) {
        return term.replace(/\%/g, "*");
    }
    return term;
};
export const getSearchObj = ({ field, op, value }: SingleField) => ({
    field,
    op,
    value,
});

export const getSearchField = ({
    term,
    field = "name",
    op = "like",
    idFieldName = "id",
    addRecordTypeFilter = false,
}) => {
    if (!term) {
        return getSearchObj({ value: `%%`, field, op });
    }

    const resourceListField = getSearchObj({ value: resourceList, field: "record_type", op: "in" });
    // if term is wrapped in single or double quotes -> exact substring search
    if (
        (term[0] === '"' && term.slice(-1) === '"') ||
        (term[0] === "'" && term.slice(-1) === "'")
    ) {
        const value = wrapTermWithWildcards(term.slice(1, -1));
        const searchField = addRecordTypeFilter
            ? {
                  and: [resourceListField, getSearchObj({ value, field, op })],
              }
            : getSearchObj({ value, field, op });

        return searchField;
    } else if (
        term.includes("*") ||
        term.includes("%") ||
        term.includes("_") ||
        term.includes("?")
    ) {
        // if term contains '*' -> wildcard search
        const value = term.replace(/\*/g, "%").replace(/\?/g, "_");
        const searchField = addRecordTypeFilter
            ? {
                  and: [resourceListField, getSearchObj({ value, field, op })],
              }
            : getSearchObj({ value, field, op });

        return searchField;
    } else {
        // replace all non-alphanumeric characters with wildcards
        let value = term.replace(/[^a-zA-Z0-9]/g, "%");
        value = wrapTermWithWildcards(value);

        if (addRecordTypeFilter) {
            const searchObj = {
                and: [
                    resourceListField,
                    {
                        or: [
                            getSearchObj({ value, field, op }),
                            getSearchObj({ field: "description", op: "like", value }),
                        ],
                    },
                ],
            };

            // if term equals a record type (resources and activities):
            if (findTermInRecords(term)) {
                return {
                    or: [
                        searchObj,
                        getSearchObj({
                            field: "record_type",
                            op: "eq",
                            value: term.toUpperCase(),
                        }),
                    ],
                };
            } else if (isUuid(term)) {
                return {
                    or: [searchObj, getSearchObj({ field: idFieldName, op: "eq", value: term })],
                };
            }

            return searchObj;
        } else {
            const searchObj = {
                or: [getSearchObj({ value, field, op })],
            };

            if (isUuid(term)) {
                searchObj.or.push(getSearchObj({ field: idFieldName, op: "eq", value: term }));
            }

            return searchObj;
        }
    }
};

export interface SingleField {
    field: string;
    op: string;
    value: any;
}

export const getFilterFieldsList = (...fields) => {
    const filters: SingleField[] = [];
    for (let field of fields) {
        if (field) {
            filters.push(field);
        }
    }
    return filters;
};

/** Serialize filter field list to JSON (`{"and":[...]}`). Unencoded — use in localStorage and APIs. */
const getFilterString = (filters) => JSON.stringify({ and: filters });

const getStatusMessageText = (message, status) => {
    if (!message) {
        if (
            status === ExecutionStatus.ENDED_SUCCESS ||
            status === ExecutionStatus.ENDED_ERROR ||
            status === ExecutionStatus.ENDED_CANCELLED
        ) {
            return "ended";
        } else {
            return "running";
        }
    } else {
        return message;
    }
};

const testHTTP = (text) => {
    const httpRegex = /^https?:\/\//;
    return httpRegex.test(text);
};

// TODO (low) use in Form.utils.ts / urlValidator
const isValidUrl = (urlString) => {
    try {
        new URL(urlString);
        return true;
    } catch (e) {
        return false;
    }
};

const getIri = (recordName, recordId) => `/${recordName?.toLowerCase()}/${recordId}`;

const checkInternalWebhookCall = (url) => {
    const currentUrlOrigin = window.location.origin;
    try {
        const { origin, pathname } = new URL(url, currentUrlOrigin);
        const webhookCallRegexp = /(\/api\/[^\/]+\/webhook\/[^\/]+\/call.*)/;
        const isWebhookCall = origin === currentUrlOrigin && webhookCallRegexp.test(pathname);
        return isWebhookCall;
    } catch {
        return false;
    }
};

const getInternalPath = (callbackUrl) => {
    let path: string | null = null;

    if (callbackUrl) {
        const currentUrlOrigin = window.location.origin;
        try {
            const { origin, pathname, search } = new URL(callbackUrl, currentUrlOrigin);
            const isWebhookCall = checkInternalWebhookCall(callbackUrl);
            if (origin === currentUrlOrigin && !isWebhookCall) {
                path = pathname + search;
            }
        } catch (e) {
            path = "";
        }
    }
    return path;
};

// A same-origin path that targets the backend REST surface (/api/...) rather than
// an SPA route. The SPA router does not own these paths, so after login they must
// be reached by a full-page navigation (window.location) rather than history.push —
// otherwise the client-side router resolves them to the catch-all error route. Used
// to return a logged-out user to a backend-served endpoint (e.g. OAuth
// /api/latest/oauth/authorize) after login.
const isBackendApiPath = (path) => typeof path === "string" && /^\/api\//.test(path);

const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);
const capitalizeFirstLetterRestLowercase = (str) => capitalizeFirstLetter(str.toLowerCase());

const getLeadingSpaces = (name) => {
    if (!name) {
        return "";
    }
    const match = name.match(/^(\s*)/);
    return match ? match[0] : "";
};
const getTrailingSpaces = (name) => {
    if (!name || name.trim() === "") {
        return "";
    }

    const match = name.match(/(\s*)$/);
    return match ? match[0] : "";
};

const getRecordLocationId = (resourceData: Resource) =>
    resourceData.project_id ?? resourceData.bundle_id ?? resourceData.workspace_id_workspace?.id;

const getRecordsLocationId = (resourceList: WrappedResource[]) => {
    if (resourceList.length === 0) return undefined;

    const firstId = getRecordLocationId(resourceList[0].resourceData);

    return resourceList.every((resource) => getRecordLocationId(resource.resourceData) === firstId)
        ? firstId
        : undefined;
};

const parseFilterSafely = (filterFromUrl: string) => {
    try {
        return JSON.parse(filterFromUrl);
    } catch (error: any) {
        console.error("Error parsing filter ", error);
        return {};
    }
};

const hashContent = (content: string) => {
    // Create a buffer from the content
    const buf = Buffer.from(content);

    // Create a simple hash from the buffer values
    let hash = 0;
    for (let i = 0; i < buf.length; i++) {
        hash = (hash << 5) - hash + buf[i];
        hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(16);
};

export {
    breakpoints,
    capitalizeFirstLetter,
    capitalizeFirstLetterRestLowercase,
    checkInternalWebhookCall,
    formatAmPm,
    formatDate,
    generateKey,
    getFilterString,
    getInternalPath,
    getIri,
    getLeadingSpaces,
    getRecordLocationId,
    getRecordsLocationId,
    getStatusMessageText,
    getTrailingSpaces,
    hashContent,
    hexToRGB,
    isBackendApiPath,
    isUuid,
    isValidUrl,
    parseFilterSafely,
    spacing,
    statusColors,
    statusLabels,
    testHTTP,
    useAnyOutsideClick,
    useInsideClick,
    useOutsideClick,
};
